// Concatenación de varios gráficos en una única animación continua (Fase 3).
// Dado un conjunto de BoardJson (uno por gráfico de un ejercicio/jugada), crea
// un único BoardJson cuya línea de tiempo reproduce primero el gráfico 1, luego
// el gráfico 2, etc. Cada gráfico reproduce SU propia animación con sus
// posiciones guardadas (positionKeyframes), una detrás de otra sin mezclar
// estados: los elementos compartidos (mismos id) saltan a la posición guardada
// de cada gráfico al arrancar su parte (el flujo "Guardar y continuar"
// garantiza continuidad: el gráfico N+1 arranca con los jugadores al final de
// sus flechas del gráfico N). Es una función pura, testeable sin Konva.

import { ARROW_KEYS, BoardArrow, BoardJson, BoardTimeline, ElementVisibilityKeyframe, PositionKeyframe, RotationKeyframe, TimelineEvent } from '../interfaces/board-json.interface';
import { buildDefaultTimeline, FramePosition } from './board-animation';

/** Combinar elementos únicos por id (la primera aparición gana). */
function mergeUnique<T extends { id: string }>(source: T[] | undefined, target: T[], seen: Set<string>): void {
  for (const el of source ?? []) {
    if (!seen.has(el.id)) {
      seen.add(el.id);
      target.push(el);
    }
  }
}

/**
 * Concatena las animaciones de varios gráficos en una sola.
 * - Elementos: se combinan por id (primera aparición gana), conservando las
 *   posiciones iniciales del primer gráfico.
 * - Flechas: se incluyen las de todos los gráficos renombrando su id con un
 *   prefijo por gráfico (evita colisiones y permite referenciarlas desde los
 *   eventos concatenados).
 * - Línea de tiempo: los eventos de cada gráfico se desplazan por la duración
 *   acumulada de los anteriores. Si un gráfico no tiene timeline explícita,
 *   se genera la por defecto (buildDefaultTimeline).
 * Devuelve null si no hay gráficos válidos o si no hay nada que animar.
 */
export function concatGraphics(graphics: Array<{ json?: BoardJson | null }>): BoardJson | null {
  const valid = graphics.filter((g) => g && g.json);
  if (valid.length === 0) return null;
  if (valid.length === 1) return valid[0].json!;

  const result: BoardJson = {
    players: [],
    playerAngles: [],
    defenders: [],
    defenderAngles: [],
    coaches: [],
    balls: [],
    cones: [],
    handoffs: [],
    texts: [],
    arrows: [],
    curvedArrows: [],
    blockArrows: [],
    curvedBlockArrows: [],
    passArrows: [],
    shotArrows: [],
    dribbleArrows: [],
    curvedDribbleArrows: [],
    courtMode: valid[0].json!.courtMode || 'full',
  };
  // Metadata de la concatenación: ids de las flechas del primer gráfico, para
  // que al volver al 0.0s tras la reproducción solo se muestre ese gráfico.
  result.firstGraphicArrows = [];

  // 1) Combinar elementos únicos por id.
  const seen = new Set<string>();
  for (const { json } of valid) {
    mergeUnique(json!.players, result.players, seen);
    mergeUnique(json!.defenders, result.defenders, seen);
    mergeUnique(json!.coaches, result.coaches, seen);
    mergeUnique(json!.balls, result.balls, seen);
    mergeUnique(json!.cones, result.cones, seen);
    mergeUnique(json!.handoffs, result.handoffs, seen);
    mergeUnique(json!.texts, result.texts, seen);
  }

  // 2) Ángulos de jugadores/defensores alineados con los elementos combinados
  //    (se toma el ángulo del primer gráfico donde aparece cada elemento).
  const playerAngleById = new Map<string, number>();
  const defenderAngleById = new Map<string, number>();
  for (const { json } of valid) {
    (json!.players ?? []).forEach((p, i) => {
      if (!playerAngleById.has(p.id)) playerAngleById.set(p.id, json!.playerAngles?.[i] ?? 0);
    });
    (json!.defenders ?? []).forEach((d, i) => {
      if (!defenderAngleById.has(d.id)) defenderAngleById.set(d.id, json!.defenderAngles?.[i] ?? 0);
    });
  }
  result.playerAngles = result.players.map((p) => playerAngleById.get(p.id) ?? 0);
  result.defenderAngles = result.defenders.map((d) => defenderAngleById.get(d.id) ?? 0);

  // 3) Flechas de todos los gráficos (renombradas con prefijo) + timeline.
  let offset = 0;
  const events: TimelineEvent[] = [];
  // Keyframes de rotación: uno por gráfico con timeline, en el instante en que
  // arranca (offset). Al llegar a la parte de un gráfico, sus jugadores/
  // defensores pasan a tener el ángulo de ese gráfico.
  const rotationKeyframes: RotationKeyframe[] = [];
  // Keyframes de visibilidad: uno por gráfico con timeline, en el instante en
  // que arranca. Solo se muestran los elementos presentes en ese gráfico (los
  // añadidos/borrados entre gráficos solo aparecen en su parte).
  const elementVisibility: ElementVisibilityKeyframe[] = [];
  // Keyframes de posición: uno por gráfico con timeline, en el instante en
  // que arranca. Cada gráfico reproduce su animación desde las posiciones
  // guardadas en él (jugadores, defensores y balones).
  const positionKeyframes: PositionKeyframe[] = [];
  let seq = 1;

  valid.forEach(({ json }, gi) => {
    const src = json!;
    const prefix = `g${gi + 1}_`;
    const renameArrowId = (id?: string | null): string | undefined => (id ? prefix + id : undefined);

    // Copiar flechas de este gráfico con id renombrado.
    for (const key of ARROW_KEYS) {
      const list = src[key] as BoardArrow[] | undefined;
      if (!list) continue;
      const target = result[key] as BoardArrow[];
      for (const a of list) {
        target.push({ ...a, id: prefix + a.id });
        // Las flechas del primer gráfico son las visibles en el estado inicial.
        if (gi === 0) result.firstGraphicArrows!.push(prefix + a.id);
      }
    }

    // Timeline de este gráfico: la explícita o la generada por defecto.
    const timeline: BoardTimeline | null = src.timeline && src.timeline.events.length > 0 ? src.timeline : buildDefaultTimeline(src);
    if (!timeline) return;

    // Keyframe de rotación de este gráfico: ángulos (radianes) por id.
    const playerAnglesById: Record<string, number> = {};
    (src.players ?? []).forEach((p, i) => {
      playerAnglesById[p.id] = src.playerAngles?.[i] ?? 0;
    });
    const defenderAnglesById: Record<string, number> = {};
    (src.defenders ?? []).forEach((d, i) => {
      defenderAnglesById[d.id] = src.defenderAngles?.[i] ?? 0;
    });
    rotationKeyframes.push({ time: offset, playerAngles: playerAnglesById, defenderAngles: defenderAnglesById });

    // Keyframe de visibilidad de este gráfico: ids de todos sus elementos.
    const visibleIds: string[] = [];
    for (const list of [src.players, src.defenders, src.coaches, src.balls, src.cones, src.handoffs, src.texts]) {
      for (const el of list ?? []) visibleIds.push(el.id);
    }
    elementVisibility.push({ time: offset, visibleIds });

    // Keyframe de posición de este gráfico: posiciones guardadas por id
    // (jugadores, defensores y balones animables).
    const positionsById: Record<string, FramePosition> = {};
    for (const list of [src.players, src.defenders, src.balls]) {
      for (const el of list ?? []) positionsById[el.id] = { x: el.x, y: el.y };
    }
    positionKeyframes.push({ time: offset, positions: positionsById });

    for (const ev of timeline.events) {
      events.push({
        ...ev,
        id: `cev_${seq++}`,
        arrowId: ev.arrowId ? renameArrowId(ev.arrowId) : ev.arrowId,
        startTime: ev.startTime + offset,
      });
    }
    offset += timeline.duration;
  });

  if (events.length === 0) return null;
  result.timeline = { duration: offset, events };
  result.rotationKeyframes = rotationKeyframes;
  result.elementVisibility = elementVisibility;
  result.positionKeyframes = positionKeyframes;
  return result;
}
